import React, { useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { StatusPill } from '../ExecutionStats/runPresentation';
import { count, duration } from '../ExecutionStats/executionStatsVocabulary';
import StageTraces from './StageTraces';
import CustomerRollupSection from './CustomerRollupSection';
import GovernanceSection from './GovernanceSection';
import { ExplainabilityDataElement, emptyOutputs, hasGovernance, mergeTechniques, metricLabel, stageStatus } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

const heading = {
    fontSize: '0.6875rem', fontWeight: 700, color: dsVar('fg-2'), textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', m: 0, mb: 1
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>{label}</Typography>
        <Typography component="div" sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), wordBreak: 'break-word' }}>
            {children}
        </Typography>
    </Box>
);

const CopyValue: React.FC<{ value: string; label: string }> = ({ value, label }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        }).catch(() => undefined);
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{value}</Box>
            <Tooltip title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`} arrow>
                <IconButton size="small" onClick={copy} aria-label={`Copy ${label.toLowerCase()}`} sx={{ color: dsVar('fg-muted'), p: 0.25 }}>
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const plural = (n: number, word: string, many = `${word}s`) => `${count(n)} ${n === 1 ? word : many}`;

/** `dict, 14 keys` / `list, 22 items` — what a stage handed on, without its contents. */
const shapeLabel = ({ type, keys, length }: { type: string; keys?: number; length?: number }) =>
    [type, keys !== undefined ? plural(keys, 'key') : null, length !== undefined ? plural(length, 'item') : null]
        .filter(Boolean)
        .join(', ');

interface StageDetailDrawerProps {
    /** The stage to show; the drawer is closed without one. */
    stage: ExplainabilityDataElement | null;
    /** The flow's final stage offers Proceed rather than Update. */
    isLastStage: boolean;
    onClose: () => void;
    /** Whether the drawer is at its wide width; the page moves the investigation agent aside for it. */
    expanded?: boolean;
    onToggleExpand?: () => void;
}

/**
 * One explainability stage in full: its outcome, then the observations and
 * decisions behind it, with the technique behind each decision.
 *
 * The stage list shows only outcomes, so this docks from the same width as the
 * Insights tab's drawer. Technique choices are held here, by decision_id, so
 * they survive moving between stages.
 */
const StageDetailDrawer: React.FC<StageDetailDrawerProps> = ({ stage, isLastStage, onClose, expanded, onToggleExpand }) => {
    const [techniqueChoices, setTechniqueChoices] = useState<Record<string, string>>({});
    const [showTechnical, setShowTechnical] = useState(false);

    const outcome = stage?.outcome;
    const techniques = mergeTechniques(stage?.techniques_applied);
    const shapes = Object.entries(outcome?.result_shape ?? {});
    const empty = emptyOutputs(outcome);
    const traces = stage?.explainability_traces ?? [];
    const rollup = stage?.customer_rollup;
    const status = stageStatus(outcome);
    // A single run's headline numbers, or a rolled-up stage's totals with their per-run range.
    const figures: { label: string; value: string; range?: string }[] = outcome?.metrics
        ? Object.entries(outcome.metrics).map(([key, m]) => ({
            label: metricLabel(key), value: count(m.sum), range: m.min === m.max ? undefined : `${count(m.min)}–${count(m.max)} per run`,
        }))
        : Object.entries(outcome?.outcome_metrics ?? {}).map(([key, value]) => ({ label: metricLabel(key), value: count(value) }));
    const failures = outcome?.errors ?? [];
    const hasOutcome = Boolean(status || outcome?.duration_secs !== undefined || stage?.scope || rollup
        || empty.length > 0 || figures.length > 0 || outcome?.error || failures.length > 0);
    const hasTechnical = shapes.length > 0 || Boolean(stage?.activity_code || stage?.node_execution_id);

    return (
        <EntityDetailDrawer
            open={Boolean(stage)}
            onClose={onClose}
            docked
            dockedMinWidth={1440}
            width={560}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
            icon={<FactCheckIcon />}
            subtitle={stage?.stage_category}
            title={stage?.stage ?? ''}
            description={outcome?.nl_description ?? ''}
        >
            {stage && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: outcome?.nl_description ? 1.5 : 0 }}>
                    {hasOutcome && (
                        <Box>
                            <Typography component="h4" sx={heading}>Outcome</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
                                {status && (
                                    <Field label="Status">
                                        <StatusPill bucket={status.bucket} status={status.raw ?? status.label} />
                                        {!status.raw && (
                                            <Box sx={{ fontSize: '0.6875rem', color: dsVar('fg-2') }}>{status.label}</Box>
                                        )}
                                    </Field>
                                )}
                                {rollup && (
                                    <Field label="Customers">
                                        {count(rollup.customers_processed)}
                                        <Box component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.6875rem' }}> · {plural(rollup.executions, 'run')}</Box>
                                    </Field>
                                )}
                                {outcome?.duration_secs !== undefined && (
                                    <Field label="Duration">{duration(outcome.duration_secs * 1000)}</Field>
                                )}
                                {stage.scope && (
                                    <Field label="Scope">
                                        <Box component="span" sx={{ textTransform: 'capitalize' }}>{stage.scope}</Box>
                                    </Field>
                                )}
                            </Box>
                            {figures.length > 0 && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, mt: 1.5 }}>
                                    {figures.map((figure) => (
                                        <Field key={figure.label} label={figure.label}>
                                            {figure.value}
                                            {figure.range && <Box sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>{figure.range}</Box>}
                                        </Field>
                                    ))}
                                </Box>
                            )}
                            {(outcome?.error || failures.length > 0) && (
                                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '8px', border: '1px solid rgba(208, 59, 59, 0.4)', backgroundColor: 'rgba(208, 59, 59, 0.08)' }}>
                                    <Typography sx={{ fontSize: '0.6875rem', color: ink('#F87171'), fontWeight: 700, mb: 0.5 }}>
                                        {outcome?.error ? 'Error' : `First ${failures.length} ${failures.length === 1 ? 'failure' : 'failures'}`}
                                    </Typography>
                                    {outcome?.error && (
                                        <Typography sx={{ fontSize: '0.75rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {outcome.error}
                                        </Typography>
                                    )}
                                    {failures.map((failure, i) => (
                                        <Typography key={i} sx={{ fontSize: '0.75rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace', wordBreak: 'break-word' }}>
                                            {failure.customer_id && <Box component="span" sx={{ color: ink('#F87171') }}>{failure.customer_id}: </Box>}
                                            {failure.error}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {empty.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 1.5, p: 1, borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)', backgroundColor: 'rgba(251, 191, 36, 0.06)' }}>
                                    <WarningAmberIcon sx={{ fontSize: 16, color: ink('#FBBF24'), mt: '1px' }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: ink('#FCD34D') }}>
                                        {empty.length === 1 ? 'One output came back empty' : `${empty.length} outputs came back empty`}
                                        {': '}
                                        <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{empty.join(', ')}</Box>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {techniques.length > 0 && (
                        <Box>
                            <Typography component="h4" sx={heading}>Techniques applied</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {techniques.map((tech, i) => (
                                    <Box
                                        key={`${tech.technique_name}-${i}`}
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, px: 1.25, py: 0.75, borderRadius: '8px', border: `1px solid ${dsVar('line')}` }}
                                    >
                                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }}>
                                            {tech.technique_name}
                                            {tech.times > 1 && <Box component="span" sx={{ color: dsVar('fg-muted'), fontWeight: 400 }}> ×{tech.times}</Box>}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-2'), whiteSpace: 'nowrap' }}>
                                            {[
                                                tech.executions !== undefined ? `in ${plural(tech.executions, 'run')}` : null,
                                                tech.input_row_count !== undefined ? plural(tech.input_row_count, 'row') : null,
                                                tech.input_feature_count !== undefined ? plural(tech.input_feature_count, 'feature') : null,
                                            ].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {traces.length > 0 && (
                        <Box>
                            <Typography component="h4" sx={heading}>
                                Observations &amp; decisions
                                {stage.trace_counts && (
                                    <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: dsVar('fg-muted') }}>
                                        {' '}· {plural(stage.trace_counts.observations, 'observation')}, {plural(stage.trace_counts.decisions, 'decision')}
                                    </Box>
                                )}
                            </Typography>
                            <StageTraces
                                traces={traces}
                                isLastStage={isLastStage}
                                techniqueChoices={techniqueChoices}
                                onChooseTechnique={(decisionId, techniqueId) =>
                                    setTechniqueChoices((prev) => ({ ...prev, [decisionId]: techniqueId }))
                                }
                            />
                        </Box>
                    )}

                    {rollup && (rollup.observations.length > 0 || rollup.decisions.length > 0) && (
                        <Box>
                            <Typography component="h4" sx={heading}>
                                Across {plural(rollup.customers_processed, 'customer')}
                                {stage.trace_counts && (
                                    <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: dsVar('fg-muted') }}>
                                        {' '}· {plural(stage.trace_counts.observations, 'observation')}, {plural(stage.trace_counts.decisions, 'decision')}
                                    </Box>
                                )}
                            </Typography>
                            <CustomerRollupSection rollup={rollup} />
                        </Box>
                    )}

                    {hasGovernance(stage.governance) && (
                        <Box>
                            <Typography component="h4" sx={heading}>
                                Governance
                                {stage.governance.policies?.length ? (
                                    <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: dsVar('fg-muted') }}>
                                        {' '}· {plural(stage.governance.policies.length, 'policy', 'policies')}
                                    </Box>
                                ) : null}
                            </Typography>
                            <GovernanceSection governance={stage.governance} />
                        </Box>
                    )}

                    {!rollup && traces.length === 0 && !outcome?.nl_description && (
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>No reasoning recorded for this step.</Typography>
                    )}

                    {hasTechnical && (
                        <Box sx={{ borderTop: `1px solid ${dsVar('line')}`, pt: 1.5 }}>
                            <ButtonBase
                                onClick={() => setShowTechnical((v) => !v)}
                                aria-expanded={showTechnical}
                                sx={{ ...heading, mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 150ms', transform: showTechnical ? 'none' : 'rotate(-90deg)' }} />
                                Technical details
                            </ButtonBase>
                            {showTechnical && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.25 }}>
                                    {shapes.length > 0 && (
                                        <Field label="Outputs">
                                            {shapes.map(([name, shape]) => (
                                                <Box key={name} sx={{ fontSize: '0.75rem' }}>
                                                    <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{name}</Box>
                                                    <Box component="span" sx={{ color: dsVar('fg-2') }}> — {shapeLabel(shape)}</Box>
                                                </Box>
                                            ))}
                                        </Field>
                                    )}
                                    {stage.activity_code && (
                                        <Field label="Activity code"><CopyValue value={stage.activity_code} label="Activity code" /></Field>
                                    )}
                                    {stage.node_execution_id && (
                                        <Field label="Execution ID"><CopyValue value={stage.node_execution_id} label="Execution ID" /></Field>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </EntityDetailDrawer>
    );
};

export default StageDetailDrawer;
